/**
 * "Vakken vullen" on the pick route page: walk the vakken of the freezer one by one and say which
 * article lies in each. The quick way to put a few hundred articles in their vak, instead of
 * opening every vak on its own. Decided outside React, like the rest of the route
 * (functions/pickRoute).
 *
 * One vak holds one article - one product in one weight (besluit 2) - and VCE and HVO sell the
 * same meat: the linked product of the other shop with the same weight goes into the same vak.
 * Putting an article in a vak is a move (planProductLink): off the spot it had, which disappears
 * when it was a spot without a code that is now empty.
 */

import { compareWeights, normaliseWeight } from "./articleCode";
import { buildEmptyEntry, itemWeight, labelMismatch, normaliseLocationCode, normalizeProductName, planProductLink, SHOP_VCE, withWeightLabel } from "./pickRoute";
import { compactProductName, findLinkForProduct, linkCounterpart } from "./productSync";

/** The ids a vak and a spot that do not exist yet carry in a plan, until Firestore gives them one. */
export const NEW_VAK_ID = "__new__";
export const DISPLACED_ID = "__displaced__";

const variantKey = (shopSource, productId, weight) => `${shopSource}|${productId}|${weight === null ? "*" : weight}`;

/**
 * The articles a vak can be filled with, one row each, as the existing link modal offers them:
 * per product its weights, or "all weights" for a product without weights or one that still has
 * its catch-all spot (it has to be split first, besluit 2). A VCE article takes the linked HVO
 * product with the same weight along, so a pair is one row. Every row says where it lies now: in
 * a vak with a code, on the route without a code, or nowhere. Those without a code come first.
 */
export const vakFillOptions = (entries, catalogue, links) => {
  const position = new Map();
  const withCatchAll = new Set();
  (entries || []).forEach((entry) =>
    (entry.products || []).forEach((item) => {
      if (!item || !item.productId) return;
      const weight = itemWeight(item);
      position.set(variantKey(item.shopSource, item.productId, weight), entry);
      if (weight === null) withCatchAll.add(`${item.shopSource}|${item.productId}`);
    }),
  );

  const variantsOf = (product) => {
    const weights = product.articles.map((article) => article.weight).filter((weight) => weight !== null);
    return weights.length === 0 || withCatchAll.has(product.key) ? [null] : weights;
  };
  const itemOf = (product, weight) => ({ shopSource: product.shopSource, productId: product.productId, name: product.name, weight });
  const placeOf = (product, weight) => {
    const entry = position.get(variantKey(product.shopSource, product.productId, weight));
    return entry ? { entryId: entry.id, location: entry.location || null, label: entry.label || "" } : null;
  };
  const optionOf = (product, weight, items) => ({
    key: variantKey(product.shopSource, product.productId, weight),
    name: product.name,
    weight,
    label: withWeightLabel(product.name, weight),
    shops: items.map((item) => item.shopSource),
    items,
    placedAt: placeOf(product, weight),
    haystack: `${compactProductName(product.name)} ${weight || ""}`,
  });

  const products = [...((catalogue && catalogue.values()) || [])];
  const byKey = new Map(products.map((product) => [product.key, product]));
  const paired = new Set();
  const options = [];

  products
    .filter((product) => product.shopSource === SHOP_VCE)
    .forEach((product) => {
      const link = findLinkForProduct(links, SHOP_VCE, product.productId);
      const reference = link ? linkCounterpart(link, SHOP_VCE) : null;
      const counterpart = reference ? byKey.get(`${reference.shopSource}|${reference.productId}`) || null : null;
      const counterpartVariants = counterpart ? variantsOf(counterpart) : [];
      variantsOf(product).forEach((weight) => {
        const items = [itemOf(product, weight)];
        if (counterpart && counterpartVariants.includes(weight)) {
          items.push(itemOf(counterpart, weight));
          paired.add(variantKey(counterpart.shopSource, counterpart.productId, weight));
        }
        options.push(optionOf(product, weight, items));
      });
    });
  products
    .filter((product) => product.shopSource !== SHOP_VCE)
    .forEach((product) =>
      variantsOf(product).forEach((weight) => {
        if (!paired.has(variantKey(product.shopSource, product.productId, weight))) options.push(optionOf(product, weight, [itemOf(product, weight)]));
      }),
    );

  const inVak = (option) => !!option.placedAt && !!option.placedAt.location;
  return options.sort(
    (a, b) =>
      Number(inVak(a)) - Number(inVak(b)) ||
      (a.name || "").localeCompare(b.name || "", "nl") ||
      compareWeights(a.weight, b.weight) ||
      a.key.localeCompare(b.key),
  );
};

/** Every word typed has to occur in the name or the weight; spacing does not matter ("kipfilet" finds "Kip filet"). */
export const filterVakFillOptions = (options, search) => {
  const needles = normalizeProductName(search || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.replace(/\s+/g, ""));
  if (needles.length === 0) return options || [];
  return (options || []).filter((option) => needles.every((needle) => option.haystack.includes(needle)));
};

/** How far the filling is: the articles that lie in a vak with a code, of all of them. */
export const vakFillProgress = (options) => ({
  placed: (options || []).filter((option) => !!option.placedAt && !!option.placedAt.location).length,
  total: (options || []).length,
});

/**
 * The writes for one step of "Vakken vullen": the vak with this code gets these items, each
 * moved off the spot it had. A vak that does not exist yet is created at the end of the route
 * (sortRouteByLocation puts it in its place). Without items it only makes sure the vak exists: an
 * empty basket is still a vak, with a label on it.
 *
 * Choosing an article says "this is what lies here". Whatever else the vak held moves to a spot
 * without a code at the end of the route, so it stays on the route - pickable with REGEL
 * BEVESTIGEN - until the walk reaches the vak it really lies in.
 *
 * Planned item by item on a working copy, because the VCE and HVO items of a pair often share one
 * old spot: the second move must see that the first one already took an item off it.
 *
 * Returns the writes and `entries`, the route as it is after them, for the cache.
 */
export const planVakFill = (entries, { location, zone } = {}, items = []) => {
  const code = normaliseLocationCode(location);
  const list = entries || [];
  const existing = list.find((entry) => normaliseLocationCode(entry.location) === code) || null;
  const first = items[0] || null;
  const label = first ? withWeightLabel(first.name, first.weight) : "";
  const zoneText = String(zone || "").trim() || null;

  let target = existing || { ...buildEmptyEntry({ location: code, zone: zoneText, label }, list), id: NEW_VAK_ID };
  let working = list;
  const sources = new Map();

  // What the vak held besides the chosen article goes to a spot of its own, without a code
  let displaced = null;
  if (existing && items.length > 0) {
    const chosen = new Set(items.map((item) => variantKey(item.shopSource, item.productId, normaliseWeight(item.weight))));
    const isChosen = (product) => chosen.has(variantKey(product.shopSource, product.productId, itemWeight(product)));
    const others = (existing.products || []).filter((product) => !isChosen(product));
    if (others.length > 0) {
      target = { ...target, products: (existing.products || []).filter(isChosen) };
      working = working.map((entry) => (entry.id === existing.id ? target : entry));
      const spot = buildEmptyEntry({ label: existing.label || withWeightLabel(others[0].name, itemWeight(others[0])) }, list);
      displaced = { ...spot, products: others, id: DISPLACED_ID };
    }
  }
  for (const item of items) {
    const plan = planProductLink(working, target, item, { weight: item.weight });
    if (!plan) continue;
    if (plan.refused) return { ok: false, reason: plan.refused, item };
    target = { ...target, products: plan.targetProducts };
    if (plan.source) {
      sources.set(plan.source.id, { id: plan.source.id, products: plan.sourceProducts, remove: plan.removeSource });
      working = plan.removeSource
        ? working.filter((entry) => entry.id !== plan.source.id)
        : working.map((entry) => (entry.id === plan.source.id ? { ...entry, products: plan.sourceProducts } : entry));
    }
  }

  // An existing vak keeps its bordje text and zone, unless its content changed hands, it had none,
  // or the text names something else than what lies there now: the 150 gram moved out and the 125
  // gram came in, and the bordje still said 150 (Sam, 2026-10-22)
  const targetUpdates = {};
  const staleLabel = !!existing && !!labelMismatch({ ...existing, products: target.products });
  if (existing && label && (!existing.label || !!displaced || staleLabel)) targetUpdates.label = label;
  if (existing && !existing.zone && zoneText) targetUpdates.zone = zoneText;
  const productsChanged = !existing || !!displaced || target.products.length !== (existing.products || []).length;
  const unchanged = !!existing && !productsChanged && sources.size === 0 && Object.keys(targetUpdates).length === 0;

  const after = existing
    ? working.map((entry) => (entry.id === existing.id ? { ...target, ...targetUpdates } : entry))
    : [...working, target];
  if (displaced) after.push(displaced);
  const withoutId = ({ id: _placeholder, ...data }) => data;

  return {
    ok: true,
    unchanged,
    location: code,
    create: existing ? null : withoutId(target),
    targetId: existing ? existing.id : null,
    targetProducts: target.products,
    targetUpdates,
    sources: [...sources.values()],
    displaced: displaced ? withoutId(displaced) : null,
    entries: after.sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
  };
};
